import { useEffect, useState } from "react";
import type { AdminReview, Invite, Stats } from "@coterie/types";
import { adminApi, api, ApiError } from "./api.ts";
import { ReviewCard } from "./ReviewCard.tsx";

const TOKEN_KEY = "coterie-admin";

const e164 = (raw: string) => {
  let s = raw.trim().replace(/[\s()-]/g, "");
  if (s.startsWith("00")) s = `+${s.slice(2)}`;
  if (/^06\d{8}$/.test(s)) s = `+31${s.slice(1)}`;
  if (s && !s.startsWith("+")) s = `+${s}`;
  return s;
};

const ago = (iso: string) => {
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? "");
  const [draft, setDraft] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("+31");
  const [isSeeder, setIsSeeder] = useState(false);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [last, setLast] = useState<Invite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [reviewCursor, setReviewCursor] = useState<string | undefined>();
  const [reviewErr, setReviewErr] = useState<string | null>(null);
  const [reviewMore, setReviewMore] = useState(false);
  const [reviewsReady, setReviewsReady] = useState(false);

  const load = async (t: string) => {
    const { invites: rows } = await adminApi.list(t);
    setInvites(rows);
  };

  const loadReviews = async (t: string, next?: string) => {
    setReviewErr(null);
    if (next) setReviewMore(true);
    try {
      const res = await adminApi.reviews(t, { cursor: next });
      setReviews((prev) => (next ? [...prev, ...res.items] : res.items));
      setReviewCursor(res.cursor);
    } catch (e) {
      setReviewErr(e instanceof Error ? e.message : "couldn't load reviews");
    } finally {
      setReviewMore(false);
      setReviewsReady(true);
    }
  };

  useEffect(() => {
    if (!token) return;
    load(token).catch((e) => {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setError(e instanceof ApiError ? e.message : "couldn't load ledger");
    });
    void loadReviews(token);
    void api.stats().then(setStats).catch(() => {});
  }, [token]);

  const unlock = async () => {
    setError(null);
    try {
      await load(draft);
      sessionStorage.setItem(TOKEN_KEY, draft);
      setToken(draft);
      setDraft("");
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403 ? "wrong token" : "couldn't unlock");
    }
  };

  const mint = async () => {
    setError(null);
    setBusy(true);
    try {
      const phoneNorm = e164(phone);
      const { code, waLink } = await adminApi.mint(token, { name: name.trim(), phone: phoneNorm, isSeeder });
      const row: Invite = {
        code,
        name: name.trim(),
        phone: phoneNorm,
        isSeeder,
        mintedBy: "admin",
        mintedAt: new Date().toISOString(),
        waLink,
      };
      setLast(row);
      setInvites((prev) => [row, ...prev]);
      setName("");
      setPhone("+31");
      setIsSeeder(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "mint failed");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="shell">
        <h1>admin</h1>
        <p className="lede">bearer token from <code>/.env</code>.</p>
        <div className="stack">
          <label>
            token
            <input
              type="password"
              autoComplete="off"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void unlock()}
            />
          </label>
          {error && <p className="err">{error}</p>}
          <button className="primary" disabled={!draft} onClick={() => void unlock()}>
            unlock
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shell wide">
      <h1>invites</h1>
      <div className="stack">
        <label>
          name
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          phone
          <input
            value={phone}
            inputMode="tel"
            placeholder="+31612345678"
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <label className="check">
          <input type="checkbox" checked={isSeeder} onChange={(e) => setIsSeeder(e.target.checked)} />
          seeder — excluded from the success metric
        </label>
        {error && <p className="err">{error}</p>}
        <button className="primary" disabled={busy || !name.trim() || phone.length < 8} onClick={() => void mint()}>
          mint
        </button>
      </div>

      {last && (
        <div className="card">
          <p className="q">{last.name}</p>
          <p className="meta">
            <code>{last.code}</code>
            {last.isSeeder ? " · seeder" : ""}
          </p>
          <a className="primary wa" href={last.waLink} target="_blank" rel="noreferrer">
            send on WhatsApp
          </a>
        </div>
      )}

      <h2 className="q">ledger</h2>
      {invites.length === 0 ? (
        <p className="note">none yet. smoke-test invites minted before this panel won't show — they lack the ledger index.</p>
      ) : (
        <ul className="ledger">
          {invites.map((row) => (
            <li key={row.code}>
              <div>
                <strong>{row.name}</strong>
                {row.isSeeder && <span className="pill">seeder</span>}
                <span className="sub">
                  {row.phone} · {ago(row.mintedAt)}
                  {row.redeemedBy
                    ? ` · @${row.redeemedBy} ${row.redeemedAt ? ago(row.redeemedAt) : ""}`
                    : " · waiting"}
                </span>
              </div>
              {!row.redeemedBy && (
                <a className="ghost" href={row.waLink} target="_blank" rel="noreferrer">
                  WhatsApp
                </a>
              )}
            </li>
          ))}
        </ul>
      )}

      <h2 className="q">reviews</h2>
      <p className="lede">
        {stats
          ? `${stats.reviewCount} reviews across ${stats.venueCount} cafés`
          : "…"}
      </p>
      {reviewErr && <p className="err">{reviewErr}</p>}
      {!reviewErr && reviewsReady && reviews.length === 0 && <p className="note">none yet.</p>}
      {reviews.length > 0 && (
        <ul className="history">
          {reviews.map((row) => (
            <ReviewCard key={`${row.createdAt}-${row.photoKey}`} {...row} />
          ))}
        </ul>
      )}
      {reviewCursor && !reviewErr && (
        <button className="primary" disabled={reviewMore} onClick={() => void loadReviews(token, reviewCursor)}>
          {reviewMore ? "loading…" : "load more"}
        </button>
      )}
    </div>
  );
}
